import React, { useEffect, useState } from "react";
import { Alert, Box, TextField, Typography } from "@mui/material";

import CriteriaTextFieldComponent from "../PolicyTranslation/TextFields/CriteriaTextFieldComponent";
import PolicyTranslationUserSelect from "../PolicyTranslation/Selects/PolicyTranslationUserSelect";
import api from "../../api";
import { useDispatch } from "react-redux";
import { setDialog, setLoading } from "slices/appSlice";
import {
  POET_AVALON_POE,
  POET_AVALON_CONFIGURATION,
} from "constants/Constants";
import PolicyTypeBanner from "components/PolicyTypeBanner";
import FirstLineContainer from "components/common/FirstLineContainer";
import { formErrorStrfromObj } from "utils/shared";
import ViewClobButton from "utils/ViewClobButton";

const TopPanelComponent = ({
  updatePolicyData,
  policyStateData,
  healthPlanDisplayName,
  policyNumber,
  policyTitle,
  editable,
  healthPlanKey,
  policyKey,
  workspaceController,
  handleCancelAction,
  policyVersion,
  children,
  policyData,
  setPolicyData,
}) => {
  const [policyConfigurators, setPolicyConfigurators] = useState([]);

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchPolicyData = async () => {
      dispatch(setLoading(true));

      try {
        const response = await api.policyData.getPolicyData(
          healthPlanKey,
          policyKey
        );
        if (response.status === 200 && response.data != null) {
          setPolicyData({
            policyConfigurator: response.data.configurationAnalyst,
            configurationStatus: response.data.configurationStatus,
            reviewType: response.data.reviewType,
            overallAdoptionDecision: response.data.overallAdoptionDecision
              ? response.data.overallAdoptionDecision
              : "",
            lobAdoptionDecisionList: response.data.lobAdoptionDecisionList
              ? response.data.lobAdoptionDecisionList
              : [],
            policyChangeControlXrefDto:
              response.data.policyChangeControlXrefDto !== null
                ? response.data.policyChangeControlXrefDto
                : { policyChangeControlXrefKey: null, changeControlText: "" },
          });
        } else if (response.data == null) {
          throw new Error("API failed to return policy data.");
        }
      } catch (e) {
        dispatch(
          setDialog({ message: formErrorStrfromObj(e), title: "Error" })
        );
      } finally {
        dispatch(setLoading(false));
      }
    };
    fetchPolicyData();
  }, []);

  //Populate drop downs
  useEffect(() => {
    const fetchUsers = async () => {
      dispatch(setLoading(true));

      try {
        // Fetch the list of users for the dropdowns
        const usersResponse = await api.healthPlanConfig.fetchAllUsers();

        setPolicyConfigurators(
          usersResponse.data.filter((user) => {
            let returnUser = false;

            if (user.poetUserAttributes.includes(POET_AVALON_CONFIGURATION)) {
              returnUser = true;
            }

            return returnUser;
          })
        );
      } catch (e) {
        dispatch(
          setDialog({
            message: e.message,
            title: "Error: Could not fetch users",
          })
        );
      } finally {
        dispatch(setLoading(false));
      }
    };
    fetchUsers();
  }, [dispatch]);

  const handleConfiguratorChange = (event) => {
    setPolicyData((prev) => ({
      ...prev,
      policyConfigurator: event.target.value,
    }));
  };

  const groupBoxSx = {
    sx: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 1,
    },
  };

  const typographyStyle = {
    component: "span",
    fontWeight: "bold",
    whiteSpace: "nowrap",
    sx: { flex: 0.5 },
  };

  const submitForReview = (submitForReview = true) => {
    const updateAssignessReviewerDto = !policyData.policyConfigurator
      ? null
      : {
          healthPlanKey: healthPlanKey,
          policyKey: policyKey,
          configuration: policyData.policyConfigurator,
          policyStage: "Configuration",
        };
    updatePolicyData(submitForReview, updateAssignessReviewerDto);
  };

  const policyTypeBannerText =
    `${healthPlanDisplayName} - ${policyVersion}` +
    (policyData.overallAdoptionDecision
      ? ` - ${policyData.overallAdoptionDecision}`
      : "");

  return (
    <>
      <FirstLineContainer>
        <Typography variant="h6" sx={{ textAlign: "left", flex: 1 }}>
          <Box component="span" fontWeight="fontWeightBold">
            Policy Rules - Use Cases:
          </Box>{" "}
          {policyNumber} - {policyTitle}
        </Typography>
        <Typography variant="h6" sx={{ textAlign: "right", flex: 1 }}>
          {workspaceController?.getPolicyHeader(0)?.PolicyType || ""}
        </Typography>
        <ViewClobButton data-testid="use-case-view-clob-button" />
      </FirstLineContainer>
      <PolicyTypeBanner
        bannerText={policyTypeBannerText}
        handleCancel={handleCancelAction}
        handleDraft={() => submitForReview(false)}
        handleReview={() => submitForReview()}
        editable={editable}
      />
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        {/* Left Column */}
        <Box
          sx={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mr: 10,
          }}
        >
          <Box {...groupBoxSx}>
            <Typography {...typographyStyle}>Review Type:</Typography>
            <Typography sx={{ flex: 1 }} component="span">
              {policyStateData?.cowName} {policyStateData?.quarter}
            </Typography>
          </Box>
          <Box {...groupBoxSx}>
            <Typography
              sx={{ flex: 0.5 }}
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              Change Control:
            </Typography>
            <TextField
              variant="outlined"
              disabled={true}
              fullWidth
              sx={{ flex: 1 }}
              value={
                policyData.policyChangeControlXrefDto?.changeControlText || ""
              }
            />
          </Box>
          <Box {...groupBoxSx}>
            <Typography
              sx={{ flex: 0.5 }}
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              Total Coverage Criteria:
            </Typography>
            <CriteriaTextFieldComponent
              sx={{ flex: 1 }}
              disabled={true}
              totalCoverageCriteria={
                workspaceController?.getRuleHeader(0)?.TotalCoverageCriteria ||
                ""
              }
            />
          </Box>
        </Box>
        {/* Center Column */}
        <Box
          sx={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mr: 10,
          }}
        >
          <Box {...groupBoxSx}>
            <Typography {...typographyStyle}>Configuration Status:</Typography>
            <Typography sx={{ flex: 1 }} component="span">
              {policyData.configurationStatus}
            </Typography>
          </Box>
          <Box {...groupBoxSx}>
            <Typography {...typographyStyle}>Configuration Analyst:</Typography>
            <PolicyTranslationUserSelect
              sx={{ flex: 1 }}
              users={policyConfigurators}
              selectedUser={policyData.policyConfigurator}
              setSelectedUser={handleConfiguratorChange}
            ></PolicyTranslationUserSelect>
          </Box>
        </Box>

        {/* Right Column */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.1,
            ml: 10,
            mb: 2,
            alignItems: "flex-end",
            textAlign: "right",
          }}
        ></Box>
      </Box>
      {children}
    </>
  );
};

export default TopPanelComponent;
